<template>
  <div
    class="segmented-switch"
    :class="['segmented-switch--' + tone, { 'is-block': block }]"
    :style="vars"
    role="group"
  >
    <span class="segmented-thumb" aria-hidden="true"></span>
    <button
      v-for="(segment, index) in segments"
      :key="index"
      class="segmented-option"
      :class="{ 'is-active': index === modelValue, 'is-icon': segment.only }"
      type="button"
      :aria-pressed="index === modelValue"
      :aria-label="segment.only ? segment.label : undefined"
      :title="segment.only ? segment.label : undefined"
      @click="$emit('update:modelValue', index)"
    >
      <font-awesome-icon v-if="segment.icon" :icon="segment.icon" aria-hidden="true" />
      <span v-if="!segment.only">{{ segment.label }}</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The one segmented switch on the site: a $line track with a sliding pill on
   it. It was the billing switch on the pricing table and nothing else, so the
   plan selector that the plan tables need on a phone had no owner to borrow
   from and was written a second time. Both read from here now, and so does the
   case page's View In control, which switches the live frame between the device
   widths the reader's screen can tell apart.

   The pill is its own element rather than a ::after carrying the selected
   label. The original painted the label inside the pseudo, which only works
   while every segment is the same width: `content: attr(data-selection)` and a
   100px thumb. A plan selector carries three labels of different lengths, and
   duplicated text has to stay pixel-aligned with the label underneath it or
   the two show through each other. So the thumb is a bare pill behind the row
   and the selected option's own label turns white. The look is unchanged.

   Two numbers drive it, both written onto the root by the component and read
   here: `--seg-count` for the thumb's width and `--seg-index` for its place.
   Nothing assumes two segments, so a third or fourth needs no rule here. */
.segmented-switch {
  background: $line;
  border: solid 2px $line;
  border-radius: $r-pill;
  display: inline-grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  position: relative;
  user-select: none;

  /* Fills its container and splits it into equal segments. The plan selector
     uses this, because it spans the table it switches. */
  &.is-block {
    display: grid;
    width: 100%;
  }

  /* A labelled segment has a floor of 6.25rem, which is the 100px the billing
     switch has always given each of its two segments. It is there so that two
     labels of different lengths still get equal segments rather than one wide
     and one narrow. A segment that is only an icon does not need it: three of
     them at that floor would be a control the width of a paragraph. A segment
     that carries an icon and its label keeps the floor, because it is a label
     like any other and the mark is what sits beside it. */
  &:not(.is-block) .segmented-option:not(.is-icon) {
    min-width: 6.25rem;
  }

  &:not(.is-block) .segmented-option.is-icon {
    min-width: 3.25rem;
  }
}

/* Phones are not a case the component answers for on its own.

   There used to be a rule here that put a labelled switch into the block form
   below md and reset the 6.25rem floor with it, because the case page's View in
   control carried three labelled segments on a phone and the floor would have
   made it wider than the container. That control is no longer rendered on a
   phone: a phone cannot tell the three device widths apart, so the page mounts
   nothing there.

   The block form is still the answer for a control that has to fill its
   container, and it stays where it has always been: the `block` prop. The plan
   selector is mounted with it, and a switch the page does not lay out itself
   should be too. A media query that turned the form on by accident of width was a
   second way to reach the same layout, and it had no caller left to serve. */

/* The dark ground. The track is the on-dark fill ladder rather than $line: a
   light grey panel is one of the brightest things a near-black band can carry
   and it read as a sheet of paper laid on the section. The labels take the same
   ladder. The pill is unchanged, because it is the selection rather than a
   surface, so it is the brand gradient on either ground. */
.segmented-switch--dark {
  background: $on-dark-fill-strong;
  border-color: $on-dark-line;

  .segmented-option {
    color: $on-dark-body;

    /* The on-dark accent, because $primary is barely above the band it would
       have to read on. It is the step the site's dark grounds already use for
       an accent, and it stays clear of the white the selection takes. */
    &:hover:not(.is-active) {
      color: $dark-accent;
    }
  }

  .segmented-option.is-active {
    color: $on-dark-strong;
  }
}

.segmented-thumb {
  background: $grad-cta;
  border-radius: $r-pill;
  bottom: 0;
  box-shadow: $shadow-card;
  left: 0;
  position: absolute;
  top: 0;
  transform: translateX(calc(var(--seg-index) * 100%));
  transition: transform $dur $ease;
  width: calc(100% / var(--seg-count));
}

/* z-index lifts the labels over the pill. The thumb is positioned, so it paints
   above in-flow content on its own, and the labels would otherwise be hidden
   under it exactly where it lands. */
.segmented-option {
  align-items: center;
  border-radius: $r-pill;
  color: $text-strong;
  cursor: pointer;
  display: flex;
  font-weight: 500;
  justify-content: center;
  padding: 0.5rem 1rem;
  position: relative;
  transition: color $dur $ease;
  z-index: 1;

  /* The step between a segment's mark and its label. Declared on the segment
     rather than as a margin on the icon, so an icon-only segment keeps its mark
     centred in the pill. */
  &:not(.is-icon) svg {
    margin-right: 0.45rem;
  }

  /* A segment that is not the selection answers the pointer by ink, which is
     the channel every other labelled control on the site has: the FAQ's
     question and the plan table's chips both move their colour and nothing
     else. A segment has no other channel here - the track owns the fill and the
     pill owns the selection - and the site's buttons deliberately do not move
     under the pointer, so nothing lifts and nothing slides.

     Declared per ground below, because the accent that reads on white is all
     but invisible on the near-black band the case page mounts this on. */
  &:hover:not(.is-active) {
    color: $primary;
  }

  /* The keyboard ring, put back because _reset.scss strips focus globally and
     leaves the ring to whoever owns the control. It is the site's canonical
     ring, the 3px $ring-focus at a 3px offset that AppButton and the pagination
     pages draw, so a segment focuses the way every other control on the site
     does. The outline follows the option's own pill radius.

     It was invisible until the reset stopped stripping the outline on keyboard
     focus as well as pointer focus, which is why nothing here declared one. */
  &:focus-visible {
    outline: 3px solid $ring-focus;
    outline-offset: 3px;
  }

  &.is-active {
    color: $on-dark-strong;
  }
}
</style>

<script>
export default {
  /* Declared so the listener does not fall through onto the root element, which
     is what Vue 3 does with an unrecognised `onUpdate:*` attribute. */
  emits: ['update:modelValue'],

  props: {
    /* The segments, in order. An entry is either the segment's own label, or an
       object for a segment that carries a mark: `{ icon, label }`, where the icon
       is drawn before the label.

       A segment with an icon and no label is icon-only: it has no visible text,
       so its label becomes its accessible name and its tooltip and it takes the
       narrower floor. A segment with both shows them together, which is what the
       case page's View in control does, so the width it acts on is named rather
       than left to three glyphs a reader has to tell apart.

       The icon is named rather than declared here so the page that owns the
       control registers the icons it needs, which is how every other icon on the
       site is reached. */
    options: {
      type: Array,
      required: true
    },
    /* The selected segment's index, for v-model. */
    modelValue: {
      type: Number,
      default: 0
    },
    /* Fills the container across equal segments. The default is inline and
       sizes to its labels, for a control sitting under a section title. */
    block: {
      type: Boolean,
      default: false
    },
    /* The ground the control stands on: light | dark. */
    tone: {
      type: String,
      default: 'light',
      validator: (value) => ['light', 'dark'].includes(value)
    }
  },

  computed: {
    /* One shape for the template, so a segment is read the same way whether the
       caller wrote a label or an icon or both. `only` is the icon-only case, and
       it is what the template and the stylesheet branch on. */
    segments() {
      return this.options.map((option) => {
        const source = typeof option === 'string' ? { label: option } : option;
        const icon = source.icon || null;
        const label = source.label || '';

        return { icon, label, only: !!icon && !label };
      });
    },

    /* The two numbers the stylesheet needs. A count and an index rather than one
       pre-computed shift, so the count also sets the width and a fourth segment
       changes nothing here. */
    vars() {
      return {
        '--seg-count': String(this.options.length),
        '--seg-index': String(this.modelValue)
      };
    }
  }
};
</script>
